import { useCallback, useEffect, useRef, useState } from 'react';
import { isIOS, isStandalone } from '../utils/push.js';

/**
 * Installation PWA (« Ajouter à l'écran d'accueil ») — logique visible mais non
 * intrusive :
 *  - capture `beforeinstallprompt` (Chromium : Android, Chrome/Edge desktop) et
 *    permet de déclencher l'installation native depuis notre propre icône ;
 *  - iOS Safari n'a pas d'API d'installation → on signale `isIOS` pour afficher
 *    des instructions « Partager → Sur l'écran d'accueil » ;
 *  - masque tout dès que l'app est installée (mode standalone ou `appinstalled`) ;
 *  - « coach-mark » unique et différé (2ᵉ session), refusable définitivement.
 *
 * Aucune bannière automatique au chargement : l'icône du header est l'invitation.
 */
const LS_INSTALLED = 'ge:pwa-installed';
const LS_COACH_DISMISSED = 'ge:pwa-coach-dismissed';
const LS_VISITS = 'ge:pwa-visits';
const SS_COUNTED = 'ge:pwa-session-counted';
const SS_COACH_SNOOZED = 'ge:pwa-coach-snoozed'; // « Plus tard » = cette session seulement
const COACH_MIN_VISITS = 2; // n'apparaît pas dès la toute première visite

function readFlag(key) {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeFlag(key) {
  try { localStorage.setItem(key, '1'); } catch { /* stockage indisponible */ }
}
function clearFlag(key) {
  try { localStorage.removeItem(key); } catch { /* stockage indisponible */ }
}
function readSessionFlag(key) {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}
function writeSessionFlag(key) {
  try { sessionStorage.setItem(key, '1'); } catch { /* stockage indisponible */ }
}

/** Compte une visite par session (pour différer le coach-mark). */
function bumpVisitsOncePerSession() {
  try {
    if (sessionStorage.getItem(SS_COUNTED)) return Number(localStorage.getItem(LS_VISITS) || 0);
    sessionStorage.setItem(SS_COUNTED, '1');
    const next = Number(localStorage.getItem(LS_VISITS) || 0) + 1;
    localStorage.setItem(LS_VISITS, String(next));
    return next;
  } catch {
    return 0;
  }
}

export function usePwaInstall() {
  // Lire le tampon global posé tôt dans main.jsx (l'événement a pu se déclencher
  // avant le montage de ce hook).
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => (typeof window !== 'undefined' ? window.__geInstallPrompt || null : null),
  );
  const [installed, setInstalled] = useState(() => isStandalone() || readFlag(LS_INSTALLED));
  const [coachDismissed, setCoachDismissed] = useState(() => readFlag(LS_COACH_DISMISSED));
  const [coachSnoozed, setCoachSnoozed] = useState(() => readSessionFlag(SS_COACH_SNOOZED));
  const [visits] = useState(() => bumpVisitsOncePerSession());
  const promptingRef = useRef(false); // anti double-clic pendant le dialogue natif
  // iOS Safari n'a pas de `beforeinstallprompt` : stable pour la session.
  const iosInstallable = isIOS() && !isStandalone() && !installed;

  // Écouteurs attachés SANS condition sur `installed` : après une désinstallation,
  // localStorage prétend encore « installé » alors que Chrome re-déclenche
  // `beforeinstallprompt` — l'événement fait foi et réactive la fonctionnalité.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault(); // supprime la bannière native du navigateur
      window.__geInstallPrompt = e;
      setDeferredPrompt(e);
      if (!isStandalone()) {
        setInstalled(false);
        clearFlag(LS_INSTALLED);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      window.__geInstallPrompt = null;
      writeFlag(LS_INSTALLED);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Un lancement en mode standalone signifie que l'app est déjà installée.
    const mq = window.matchMedia?.('(display-mode: standalone)');
    const onDisplayChange = (ev) => { if (ev.matches) onInstalled(); };
    mq?.addEventListener?.('change', onDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq?.removeEventListener?.('change', onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt || promptingRef.current) return 'unavailable';
    promptingRef.current = true;
    try {
      // `prompt()` jette (InvalidStateError) sur un événement déjà consommé :
      // il doit rester DANS le try, et l'événement est purgé quoi qu'il arrive.
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
        writeFlag(LS_INSTALLED);
        return 'accepted';
      }
      return 'dismissed';
    } catch {
      return 'dismissed';
    } finally {
      promptingRef.current = false;
      setDeferredPrompt(null); // à usage unique
      window.__geInstallPrompt = null;
    }
  }, [deferredPrompt]);

  /** « Ne plus proposer » — définitif (localStorage). */
  const dismissCoach = useCallback(() => {
    setCoachDismissed(true);
    writeFlag(LS_COACH_DISMISSED);
  }, []);

  /** « Plus tard » — cette session seulement (sessionStorage) : reviendra. */
  const snoozeCoach = useCallback(() => {
    setCoachSnoozed(true);
    writeSessionFlag(SS_COACH_SNOOZED);
  }, []);

  const canPromptNative = Boolean(deferredPrompt);
  const installable = !installed && (canPromptNative || iosInstallable);
  // Coach-mark : visible, différé (≥2ᵉ session), refusable pour la session ou définitivement.
  const showCoach = installable && !coachDismissed && !coachSnoozed && visits >= COACH_MIN_VISITS;

  return {
    installable,
    installed,
    canPromptNative,
    isIOS: iosInstallable,
    promptInstall,
    showCoach,
    dismissCoach,
    snoozeCoach,
  };
}

export default usePwaInstall;
